import React, { useState, useMemo } from 'react';
import { Card, Table, Tag, Button, Select, Space, Empty, App, Row, Col } from 'antd';
import {
  DownloadOutlined,
  FileTextOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { trpc } from '../../lib/trpc';
import type { Company } from '../../types/company';
import dayjs from 'dayjs';

const { Option } = Select;

interface InvoicesPanelProps {
  company?: Company;
}

type InvoiceStatus = 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';

interface Invoice {
  id: string;
  number: string | null;
  created: number;
  issuedAt?: number | null;
  amountDue: number;
  amountPaid: number;
  currency: string;
  status: InvoiceStatus | null;
  invoicePdf: string | null;
  hostedInvoiceUrl: string | null;
  dueDate: number | null;
  paidAt: number | null;
}

const InvoicesPanel: React.FC<InvoicesPanelProps> = ({ company }) => {
  const { message } = App.useApp();
  // デフォルトを'all'に設定してすべての請求書を表示
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | 'all'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;  // Draft請求書を確実に含めるためlimitを増やす

  const {
    data,
    isLoading,
    refetch,
    isRefetching,
  } = trpc.stripe.listInvoices.useQuery(
    {
      status: statusFilter === 'all' ? undefined : statusFilter,
      limit: pageSize,
    },
    {
      enabled: !!company?.stripeCustomerId,
      staleTime: 5 * 60 * 1000, // 5分間キャッシュ
    }
  );

  // Draft請求書を優先的に表示（ステータス優先度 + 日付順）
  // 0円の請求書は除外
  const invoices = useMemo(() => {
    const rawInvoices = (data?.invoices ?? []) as Invoice[];

    // 0円の請求書を除外
    const filteredInvoices = rawInvoices.filter((invoice) => invoice.amountDue > 0);

    // ステータスの優先順位
    const statusPriority: Record<string, number> = {
      draft: 0,      // 最優先（次回請求書）
      open: 1,       // 未払い請求書
      paid: 2,       // 支払済み
      uncollectible: 3,
      void: 4,
    };

    return [...filteredInvoices].sort((a, b) => {
      // 1. ステータス優先度でソート
      const aPriority = statusPriority[a.status ?? ''] ?? 99;
      const bPriority = statusPriority[b.status ?? ''] ?? 99;
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }

      // 2. 同じステータスなら発行日でソート（新しい順）。未発行のdraft同士は作成日で安定化する。
      return compareIssueDateDesc(a, b);
    });
  }, [data?.invoices]);


  if (!company) {
    return <div>会社情報を読み込み中...</div>;
  }

  if (!company.stripeCustomerId) {
    return (
      <Card>
        <Empty
          description="Stripe連携が設定されていません"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <p>契約プランを開始すると、請求書がこちらに表示されます。</p>
        </Empty>
      </Card>
    );
  }

  const handleDownloadPdf = (invoice: Invoice) => {
    if (invoice.invoicePdf) {
      window.open(invoice.invoicePdf, '_blank');
    } else if (invoice.hostedInvoiceUrl) {
      message.info('PDFはまだ生成されていません。請求書ページを開きます。');
      window.open(invoice.hostedInvoiceUrl, '_blank');
    } else {
      message.warning('請求書URLが取得できません');
    }
  };

  const getStatusTag = (status: InvoiceStatus | null) => {
    const statusMap: Record<string, { color: string; text: string }> = {
      paid: { color: 'green', text: '支払済み' },
      open: { color: 'blue', text: '未払い' },
      draft: { color: 'default', text: '下書き' },
      void: { color: 'red', text: '無効' },
      uncollectible: { color: 'orange', text: '回収不能' },
    };

    const config = status ? statusMap[status] : { color: 'default', text: '不明' };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const getDocumentType = (status: InvoiceStatus | null) => {
    if (status === 'paid') return '領収書';
    if (status === 'draft') return '次回請求書';
    if (status === 'open') return '請求書';
    if (status === 'void') return '無効';
    if (status === 'uncollectible') return '回収不能';
    return '請求書';
  };

  const formatCurrency = (amount: number, currency: string) => {
    // Zero-decimal currencies（0桁小数の通貨）は100で割らない
    // JPY, KRW, VND, CLP などは既に最小単位が「円」「ウォン」など
    const zeroDecimalCurrencies = ['jpy', 'krw', 'vnd', 'clp'];
    const isZeroDecimal = zeroDecimalCurrencies.includes(currency.toLowerCase());

    // JPYなどは100で割らない、USDなどは100で割る（cent単位のため）
    const displayAmount = isZeroDecimal ? amount : amount / 100;

    if (currency === 'jpy') {
      return `¥${displayAmount.toLocaleString('ja-JP')}`;
    }
    return `${displayAmount.toLocaleString()} ${currency.toUpperCase()}`;
  };

  const formatDate = (timestamp: number) => {
    return dayjs.unix(timestamp).format('YYYY年MM月DD日');
  };

  const formatOptionalDate = (timestamp: number | null | undefined) => {
    return timestamp == null ? '-' : formatDate(timestamp);
  };

  const columns = [
    {
      title: '請求書番号',
      dataIndex: 'number',
      key: 'number',
      render: (num: string | null) => num || '-',
    },
    {
      title: '種別',
      dataIndex: 'status',
      key: 'type',
      render: (status: InvoiceStatus | null) => getDocumentType(status),
    },
    {
      title: '発行日',
      dataIndex: 'issuedAt',
      key: 'issuedAt',
      render: (timestamp: number | null) => formatOptionalDate(timestamp),
      sorter: compareIssueDateDesc,
      defaultSortOrder: 'ascend' as const,
    },
    {
      title: '金額',
      dataIndex: 'amountDue',
      key: 'amount',
      render: (amount: number, record: Invoice) => formatCurrency(amount, record.currency),
      align: 'right' as const,
    },
    {
      title: 'ステータス',
      dataIndex: 'status',
      key: 'status',
      render: (status: InvoiceStatus | null) => getStatusTag(status),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: unknown, record: Invoice) => (
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={() => handleDownloadPdf(record)}
          disabled={!record.invoicePdf && !record.hostedInvoiceUrl}
        >
          PDF
        </Button>
      ),
    },
  ];

  return (
    <Card
      title={
        <Space>
          <FileTextOutlined />
          請求書・領収書
        </Space>
      }
      extra={
        <Button
          icon={<ReloadOutlined />}
          onClick={() => refetch()}
          loading={isRefetching}
        >
          更新
        </Button>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* フィルター */}
        <Row gutter={16}>
          <Col>
            <Select
              placeholder="ステータスで絞り込み"
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value);
                setCurrentPage(1);
              }}
              style={{ width: 200 }}
            >
              <Option value="all">すべて</Option>
              <Option value="paid">領収書（支払済み）</Option>
              <Option value="open">請求書（未払い）</Option>
              <Option value="draft">下書き</Option>
            </Select>
          </Col>
        </Row>

        {/* 一覧テーブル */}
        <Table
          loading={isLoading}
          dataSource={invoices}
          columns={columns}
          rowKey="id"
          rowClassName={(record) => {
            // Draft請求書を強調表示
            if (record.status === 'draft') {
              return 'draft-invoice-row';
            }
            return '';
          }}
          pagination={{
            current: currentPage,
            pageSize,
            total: invoices.length,
            onChange: setCurrentPage,
            showSizeChanger: false,
            showTotal: (total) => `全 ${total} 件`,
          }}
          locale={{
            emptyText: (
              <Empty
                description="請求書がありません"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                <p>Stripeで請求が発生すると、こちらに表示されます。</p>
              </Empty>
            ),
          }}
        />

        {/* 注意事項 */}
        <div style={{ fontSize: '12px', color: '#666', marginTop: '16px' }}>
          <p>
            ※ <strong>次回請求書（下書き）</strong>は、請求確定前に自動的に作成され、一覧の最上部に表示されます。
          </p>
          <p>
            ※ PDFが生成されていない場合は、Stripeの請求書ページが開きます。
          </p>
          <p>
            ※ 領収書は支払済みの請求書として表示されます。
          </p>
          <p>
            ※ 最新の情報が反映されていない場合は、右上の「更新」ボタンを押してください。
          </p>
        </div>

        {/* Draft請求書用のスタイル */}
        <style>{`
          .draft-invoice-row {
            background-color: #fff7e6 !important;
            border-left: 3px solid #faad14;
          }
          .draft-invoice-row:hover {
            background-color: #ffe7ba !important;
          }
        `}</style>
      </Space>
    </Card>
  );
};

function compareIssueDateDesc(a: Invoice, b: Invoice) {
  const aIssuedAt = a.issuedAt ?? null;
  const bIssuedAt = b.issuedAt ?? null;

  if (aIssuedAt === null && bIssuedAt === null) {
    return b.created - a.created;
  }

  if (aIssuedAt === null) {
    return 1;
  }

  if (bIssuedAt === null) {
    return -1;
  }

  return bIssuedAt - aIssuedAt;
}

export default InvoicesPanel;
